import React from 'react'

import { Link } from 'react-router-dom'

import { Box, Card, ClassNameMap, Grid, List, ListItem, makeAppStyles, Money, Typography, useAppTheme } from '@percept/mui'

import { OpenInNew } from '@percept/mui/icons'

import { PerformanceDetailCard, PerformanceDetailCardContent } from './PerformanceDetailCard'

import { Bubble } from 'components/CampaignSetup/BubbleChart'

import { scaleLinear } from '@visx/scale'

import { sortBy } from 'lodash-es'

import { getComparisonFunction, getPercentageDelta } from '@percept/utils'

import { percentageDeltaFormatter } from '@percept/mui/charts'

import { providerLabelMap } from '@percept/constants'

import { ReportProviderV2 } from '@percept/types'

import { OrgUnitPerformanceDatum } from './typings'


export type MultiOrgUnitPerformanceCardProps = {
  domain: [number, number]
  currency?: string
  provider: ReportProviderV2
  segment: string | null
  metric: string
  improved: OrgUnitPerformanceDatum[]
  worse: OrgUnitPerformanceDatum[]
}

const useStyles = makeAppStyles( theme => ({
  collatedCard: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: theme.spacing(1, 3),
  },
  collatedCardTitle: {
    height: '100%',
  },
  collatedCardContent: {
    padding: theme.spacing(1, 0),
    marginRight: theme.spacing(8),
    '&:last-of-type': {
      marginRight: theme.spacing(0),
    }
  },
  collatedGridContainer: {
    display: 'flex',
    flexGrow: 1,
    flexWrap: 'wrap',
  },
  collatedGridItem: {
    display: 'flex',
    paddingBottom: 'unset',
    paddingTop: 'unset',
    marginRight: theme.spacing(8),
    '&:last-of-type': {
      marginRight: theme.spacing(0),
    }
  },
  content: {
    display: 'flex',
    justifyContent: 'space-evenly',
    height: 64,
  },
  count: {
    position: 'absolute',
    userSelect: 'none',
    pointerEvents: 'none',
    fontSize: 12,
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.background.paper,
  },
  bubble: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing(2),
    '&:last-of-type': {
      marginRight: 0,
    },
  },
  bubbleContent: {
    position: 'relative',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  svg: {
    cursor: 'pointer',
    transition: theme.transitions.create('filter'),
    filter: 'drop-shadow(0 0 6px transparent)',
    '&:hover': {
      filter: `drop-shadow(0 0 6px rgba(0,0,0,${theme.palette.type === 'dark' ? 1 : 0.5}))`,
    },
  },
  improved: {
    color: theme.chart.healthColourScale(1),
  },
  worse: {
    color: theme.chart.healthColourScale(0),
  },
  orgLink: {
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    marginLeft: theme.spacing(0.5),
    color: 'inherit',
  },
  linkIcon: {
    marginRight: theme.spacing(2),
  },
  linkValue: {
    marginLeft: 'auto',
    paddingLeft: theme.spacing(2),
    fontWeight: theme.typography.fontWeightBold,
  },
  changeValueNegative: {
    marginLeft: theme.spacing(1.5),
    color: theme.chart.healthColourScale(0),
  },
  changeValuePositive: {
    marginLeft: theme.spacing(1.5),
    color: theme.chart.healthColourScale(1),
  },
  percentageDelta: {
    marginLeft: theme.spacing(1),
  },
}))


const comparisonFunction = getComparisonFunction('ABSOLUTE')

type PopoverClassName = (
  | 'orgLink'
  | 'linkIcon'
  | 'linkValue'
  | 'percentageDelta'
)

const PopoverContent = ({
  title,
  data,
  classes,
  currency,
  changeValueClassName,
}: {
  title: React.ReactNode
  data: OrgUnitPerformanceDatum[]
  classes: ClassNameMap<PopoverClassName>
  currency?: string
  changeValueClassName?: string
}): JSX.Element => {
  return (
    <>
    <Box px={2} mt={2}>
      { title }
    </Box>
    <List>
      { sortBy(data, 'org_unit_name').map( d => (
        <ListItem
          key={d.org_unit_id}
          component={Link}
          className={classes.orgLink}
          button
          to={`/dashboards/${d.org_unit_id}/campaign-setup`}>
          <OpenInNew fontSize='small' className={classes.linkIcon} />
          { d.org_unit_name }
          { d.currentValue !== null && (
            <div className={classes.linkValue}>
              <Money amount={d.currentValue} currency={currency} />
              { d.previousValue !== null && (
                <span className={changeValueClassName}>
                  <Money
                    amount={comparisonFunction(d.currentValue, d.previousValue)}
                    currency={currency}
                    forceRenderSign />
                  <span className={classes.percentageDelta}>
                    ({ percentageDeltaFormatter(getPercentageDelta(d.currentValue, d.previousValue)) })
                  </span>
                </span>
              )}
            </div>
          )}
        </ListItem>
      ))}
    </List>
    </>
  )
}

export const MultiOrgUnitPerformanceCard = ({
  provider,
  currency,
  domain,
  segment,
  metric,
  improved,
  worse,
}: MultiOrgUnitPerformanceCardProps) => {
  const classes = useStyles()
  const appTheme = useAppTheme()
  const scale = scaleLinear({ range: [30, 90], domain })
  return (
    <PerformanceDetailCard
      provider={provider}
      segment={segment}
      metric={metric}>
      <div className={classes.content}>
        { !!improved.length && (
          <div className={classes.bubble}>
            <Bubble
              columnKey=''
              className={classes.bubbleContent}
              data={improved}
              size={`${scale(improved.length)}%`}
              color={appTheme.chart.healthColourScale(1)}
              classes={classes}
              getPopoverContent={({ data }) => {
                return (
                  <PopoverContent
                    title={`Better ${metric}`}
                    data={data}
                    classes={classes}
                    currency={currency}
                    changeValueClassName={classes.changeValuePositive} />
                )
              }} />
          </div>
        )}
        { !!worse.length && (
          <div className={classes.bubble}>
            <Bubble
              columnKey=''
              className={classes.bubbleContent}
              data={worse}
              size={`${scale(worse.length)}%`}
              color={appTheme.chart.healthColourScale(0)}
              classes={classes}
              getPopoverContent={({ data }) => {
                return (
                  <PopoverContent
                    title={`Worse ${metric}`}
                    data={data}
                    classes={classes}
                    currency={currency}
                    changeValueClassName={classes.changeValueNegative} />
                )
              }} />
          </div>
        )}
      </div>
    </PerformanceDetailCard>
  )
}


export type MultiOrgUnitPerformanceCardCollatedProps = (
  Pick<MultiOrgUnitPerformanceCardProps, 'domain' | 'currency' | 'provider'>
  & {
    data: (
      Pick<MultiOrgUnitPerformanceCardProps, 'segment' | 'metric' | 'improved' | 'worse'>
    )[]
  }
)

export const MultiOrgUnitPerformanceCardCollated = ({
  provider,
  currency,
  domain,
  data,
}: MultiOrgUnitPerformanceCardCollatedProps) => {
  const classes = useStyles()
  const appTheme = useAppTheme()
  const scale = scaleLinear({ range: [30, 90], domain })
  return (
    <Card
      className={classes.collatedCard}
      elevation={3}>
      <Typography variant='h5'>
        { providerLabelMap[provider] }
      </Typography>
      <div className={classes.collatedGridContainer}>
        { data.map( ({ segment, metric, improved, worse }) => (
          <div
            key={segment || metric}
            className={classes.collatedGridItem}>
            <PerformanceDetailCardContent
              className={classes.collatedCardContent}
              title={segment}
              // subtitle={
              //   segment ? `${segment} ${metric}` : metric
              // }
              subtitle={metric}>
              <div className={classes.content}>
                { !!improved.length && (
                  <div className={classes.bubble}>
                    <Bubble
                      columnKey=''
                      className={classes.bubbleContent}
                      data={improved}
                      size={`${scale(improved.length)}%`}
                      color={appTheme.chart.healthColourScale(1)}
                      classes={classes}
                      getPopoverContent={({ data }) => {
                        return (
                          <PopoverContent
                            title={`Better ${metric}`}
                            data={data}
                            classes={classes}
                            currency={currency}
                            changeValueClassName={classes.changeValuePositive} />
                        )
                      }} />
                  </div>
                )}
                { !!worse.length && (
                  <div className={classes.bubble}>
                    <Bubble
                      columnKey=''
                      className={classes.bubbleContent}
                      data={worse}
                      size={`${scale(worse.length)}%`}
                      color={appTheme.chart.healthColourScale(0)}
                      classes={classes}
                      getPopoverContent={({ data }) => {
                        return (
                          <PopoverContent
                            title={`Worse ${metric}`}
                            data={data}
                            classes={classes}
                            currency={currency}
                            changeValueClassName={classes.changeValueNegative} />
                        )
                      }} />
                  </div>
                )}
              </div>
            </PerformanceDetailCardContent>
          </div>
        ))}
      </div>
    </Card>
  )
}